import type { SceneArc, SceneNode } from "./globe-scene-types";
import type { Camera } from "./globe-projection";
import type { Box } from "./globe-labels";

/** The memory globe's canvas (spec §7.1), ported from the mockup's drawGlobe, strokeArcs, drawSpokes, drawGlobeNode, the label
 * drawing, globeNodeAt and globeFactAt. Colours are the standard's variables read from the canvas; an avatar first clears the
 * lines beneath it (destination-out), then fills with the text colour. */
/** Unlit things fade to .15 while something is in focus (spec §7.1). */
export const DIM = 0.15;
export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}
export interface GlobePalette {
  ink: Rgba;
  muted: Rgba;
  amber: Rgba;
  accent: Rgba;
}
export interface Point {
  x: number;
  y: number;
}
export interface GraphArea {
  cx: number;
  cy: number;
  radius: number;
}
export interface ProjectedNode extends SceneNode {
  x: number;
  y: number;
  z: number;
  half: number;
  scale: number;
  depthAlpha: number;
  tempAlpha: number;
  vis: number;
}
/** `points`: x, y, z triples along an arc, empty for a spoke from You. `nodes` are depth-sorted, back first. */
export interface ProjectedArc {
  arc: SceneArc;
  points: number[];
}
export interface ProjectedScene {
  nodes: ProjectedNode[];
  byKey: ReadonlyMap<string, ProjectedNode>;
  arcs: ProjectedArc[];
  focusKey: string | null;
  dense: boolean;
  camera: Camera;
  dimmed: number;
}
/** Eased visual state (mockup GL.lit, the temporary things' alpha, GL.dimmed). */
export interface Fades {
  lit: Map<string, number>;
  temp: Map<string, number>;
  dimmed: number;
}
/** `hotArcKeys`: the arc the pocket shows and the hovered one; `pocket`: the pocket inside the frame, which labels avoid. */
export interface DrawOptions {
  palette: GlobePalette;
  width: number;
  height: number;
  hoverKey: string | null;
  hotArcKeys: ReadonlySet<string>;
  pocket: Box | null;
  /** A Pinned tile is too small for the globe's labels: only the hovered
   * thing is named. */
  hoverLabelOnly: boolean;
}
